/**
 * The permission layer's telemetry taxonomy: event names and the attribute vocabulary, and nothing
 * else.
 *
 * Deliberately outside `shadow/`, which U18 deletes whole. The shadow comparison is scaffolding; the
 * outage facet U17 stands a monitor on is not, and a monitor whose facet disappeared with the
 * scaffolding would fail silently at exactly the wrong moment.
 *
 * `monitoring.logEvent`, not `trackAction` (KTD12). `trackAction`'s name is typed against a closed
 * `Namespace` union with no permissions namespace, and `logEvent` writes attributes flat at the
 * log's top level specifically so they read as facets for a log-based metric - which is what an
 * agreement rate is.
 *
 * Pure strings and types, no store or React imports, so a hook and a Redux listener can share them.
 */

export const PermissionsTelemetry = {
  /**
   * The permissions query settled as unanswerable, emitted once per session. U17's squad-owned
   * monitor stands on this facet.
   *
   * The service's own SLO alerts cannot fire for a frontend-side cause - a token-audience mismatch,
   * a CORS rejection, a missing base URL - because those requests either never arrive or are
   * answered correctly.
   */
  unanswerable: 'permissions.unanswerable',
} as const;

/**
 * The shadow comparison's two events are gone with the comparator (U18).
 *
 * `permissions.shadow_comparison_outcome` and `permissions.shadow_mismatch` no longer have an
 * emitter. Any Datadog monitor or dashboard on those facets will go quiet rather than alert, so
 * they are named here once, plainly, for whoever finds an empty graph: they were the shadow phase's
 * evidence and the shadow phase is over.
 */
export const RETIRED_SHADOW_EVENTS = ['permissions.shadow_comparison_outcome', 'permissions.shadow_mismatch'] as const;

/** Which target the answer was about. Flat, so it reads as a facet. */
export type TelemetryScopeKind = 'community' | 'organisation';

export type UnanswerableAttributes = {
  scopeKind: TelemetryScopeKind;
  status: number;
  failureClass: string;
};
